/**
 * The daily wellness check-in — the four Hooper index items
 * (Hooper & Mackinnon 1995), one entry per user per day.
 *
 * Scale direction: **1 is best and 7 is worst** on every item, so
 * `sleep_quality: 7` is very poor sleep and `hooper_index` is the four
 * summed (4–28). It is the only fatigue-related input the app reads; nothing
 * is scored or prescribed from it.
 *
 * - `GET  /user/{username}/wellness?days=1..90` → `WellnessEntry[]`, newest first
 * - `POST /user/{username}/wellness` → `WellnessEntry` (201 new day, 200 replaced)
 * - `DELETE /wellness/{id}` → 204
 *
 * Storing or deleting an entry invalidates the recovery-status cache, so
 * `sore_today` on the Today view and recovery status update on the next read.
 */

/** The four items, in the order the check-in asks them. */
export type WellnessItem = 'fatigue' | 'soreness' | 'stress' | 'sleep_quality';

export const WELLNESS_ITEMS: readonly WellnessItem[] = [
  'fatigue',
  'soreness',
  'stress',
  'sleep_quality',
] as const;

/** The lowest and highest rating on every item. */
export const WELLNESS_SCALE = { min: 1, max: 7 } as const;

/** The longest note the API accepts. */
export const WELLNESS_NOTES_MAX = 530;

export interface WellnessEntryInput {
  /** YYYY-MM-DD; defaults to today and must not be in the future. */
  date?: string;
  /** 1–7, 1 best. */
  fatigue: number;
  /** 1–7, 1 best. */
  soreness: number;
  /** 1–7, 1 best. */
  stress: number;
  /** 1–7, 1 best. */
  sleep_quality: number;
  /** Canonical `MuscleGroup` values; duplicates are removed server-side. */
  sore_muscles?: string[];
  /** ≤ 530 characters. */
  notes?: string;
}

export interface WellnessEntry {
  /** Hashid; the `DELETE /wellness/{id}` handle. */
  id: string;
  /** YYYY-MM-DD. */
  date: string;
  fatigue: number;
  soreness: number;
  stress: number;
  sleep_quality: number;
  /** The four summed, 4–28. */
  hooper_index: number;
  sore_muscles: string[];
  notes: string | null;
}
